import React, { useState } from 'react';
import { parse } from 'papaparse';
import { db } from '../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import { useTheme } from '@mui/material/styles';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDBox from 'components/MDBox';
import { Container, Paper, Grid, FormControl, InputLabel, Select, MenuItem, Tooltip, Table, TableBody, TableCell, TableHead, TableRow, Alert } from '@mui/material';

const CSVUpload = ({ userID, tradeAccounts, selectedAccountID, setSelectedAccountID }) => {
  const theme = useTheme();
  const [csvData, setCsvData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [fieldMapping, setFieldMapping] = useState({});
  const [detectedFormat, setDetectedFormat] = useState(null);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [previewRows, setPreviewRows] = useState([]);

  const availableFields = {
    entryDateTime: '',
    exitDateTime: '',
    symbol: '',
    entry: '',
    exit: '',
    sl: '',
    tp: '',
    returns: '',
    leverage: '',
    lotSizeEntry: '',
    sizeType: 'lot',
    shortLong: '',
    commissionValue: '',
    exchangeRate: '',
    comments: 'Auto-Uploaded Data',
    status: 'Closed',
  };

  const brokerFormats = {
    metaTrader: {
      'Open Time': 'entryDateTime',
      'Close Time': 'exitDateTime',
      'Type': 'shortLong',
      'Size (Volume)': 'lotSizeEntry',
      'Symbol': 'symbol',
      'Open Price': 'entry',
      'Close Price': 'exit',
      'Stop Loss (SL)': 'sl',
      'Take Profit (TP)': 'tp',
      'Commission': 'commissionValue',
      'Profit': 'returns',
      'Leverage': 'leverage',
    },
    ftmo: {
      'Open': 'entryDateTime',
      'Type': 'shortLong',
      'Volume': 'lotSizeEntry',
      'Symbol': 'symbol',
      'Price_1': 'exit',
      'SL': 'sl',
      'TP': 'tp',
      'Close': 'exitDateTime',
      'Price': 'entry',
      'Commissions': 'commissionValue',
      'Profit': 'returns',
    },
    equityEdge: {
      'Symbol': 'symbol',
      'Open time': 'entryDateTime',
      'Volume': 'lotSizeEntry',
      'Side': 'shortLong',
      'Close time': 'exitDateTime',
      'Open price': 'entry',
      'Close Price': 'exit',
      'Stop loss': 'sl',
      'Take profit': 'tp',
      'Commission': 'commissionValue',
      'Profit': 'returns',
    },
    topOneTrader: {
      'Symbol': 'symbol',
      'Side': 'shortLong',
      'SL': 'sl',
      'TP': 'tp',
      'Open Time': 'entryDateTime',
      'Open Price': 'entry',
      'Close Time': 'exitDateTime',
      'Close Price': 'exit',
      'Lots': 'lotSizeEntry',
      'Commissions': 'commissionValue',
      'PnL': 'returns',
    },
  };

const parseCustomDate = (dateString) => {
  if (!dateString) return null;

  // Try parsing YYYY-MM-DD HH:MM:SS (FTMO format)
const regexISO = /^(\d{4})-(\d{2})-(\d{2})\s(\d{2}):(\d{2})(?::(\d{2}))?$/;
const matchISO = dateString.match(regexISO);
if (matchISO) {
  const [_, year, month, day, hour, minute, second] = matchISO;
  const date = new Date(year, month - 1, day, hour, minute, second ? Number(second) : 0);
  return isNaN(date.getTime()) ? null : date;
}

// Try parsing DD/MM/YYYY HH:MM or DD/MM/YYYY HH:MM:SS
const regexDDMMYYYY = /^(\d{2})\/(\d{2})\/(\d{4})\s(\d{2}):(\d{2})(?::(\d{2}))?$/;
const matchDDMMYYYY = dateString.match(regexDDMMYYYY);
if (matchDDMMYYYY) {
  const [_, day, month, year, hour, minute, second] = matchDDMMYYYY;
  const date = new Date(year, month - 1, day, hour, minute, second ? Number(second) : 0);
  return isNaN(date.getTime()) ? null : date;
}

  return null;
};

const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  setError(null);
  parse(file, {
    complete: (result) => {
      if (!result.data.length || Object.keys(result.data[0]).length === 0) {
        setError('Empty or invalid CSV file.');
        return;
      }

      // Log raw parsed data for debugging
      console.log('Raw parsed CSV data:', result.data);

      // Get headers to detect broker format early
      const headers = Object.keys(result.data[0]).map(h => h.trim());

      // Calculate match percentage for broker detection
      const calculateMatchPercentage = (formatHeaders) => {
        const normalizedHeaders = headers.map(h => h.toLowerCase().replace(/[\s\/]/g, ''));
        const normalizedFormatHeaders = Object.keys(formatHeaders).map(h => h.toLowerCase().replace(/[\s\/]/g, ''));
        const matchCount = normalizedFormatHeaders.filter(header => normalizedHeaders.includes(header)).length;
        return (matchCount / normalizedFormatHeaders.length) * 100;
      };

      const matchScores = {
        metaTrader: calculateMatchPercentage(brokerFormats.metaTrader),
        ftmo: calculateMatchPercentage(brokerFormats.ftmo),
        equityEdge: calculateMatchPercentage(brokerFormats.equityEdge),
        topOneTrader: calculateMatchPercentage(brokerFormats.topOneTrader),
      };
      console.log('Match Scores:', matchScores);

      const bestMatch = Object.entries(matchScores).reduce((a, b) => (a[1] > b[1] ? a : b));
      const detectedBroker = bestMatch[1] >= 80 ? bestMatch[0] : null;

      // Define required fields based on detected broker
// Helpers to match headers case-insensitively / spacing-insensitively
const normalizeHeader = (s) => String(s || '').trim().toLowerCase().replace(/[\s\/]/g, '');
const getRowValueByHeader = (row, wantedHeader) => {
  const wanted = normalizeHeader(wantedHeader);
  const key = Object.keys(row || {}).find((k) => normalizeHeader(k) === wanted);
  return key ? row[key] : undefined;
};

// Define required fields based on detected broker
let requiredFields = ['Symbol']; // Default required field
if (detectedBroker === 'ftmo') {
  requiredFields = ['Open', 'Close', 'Symbol'];
} else if (detectedBroker === 'topOneTrader') {
  requiredFields = ['Open Time', 'Close Time', 'Symbol'];
} else if (detectedBroker === 'metaTrader') {
  requiredFields = ['Open Time', 'Close Time', 'Symbol'];
} else if (detectedBroker === 'equityEdge') {
  requiredFields = ['Open time', 'Close time', 'Symbol'];
}

// Filter out invalid rows
const filteredData = result.data.filter((row, index) => {
  // Check for required fields (case-insensitive)
  const hasRequiredFields = requiredFields.every((field) => {
    const v = getRowValueByHeader(row, field);
    return v !== '' && v !== null && v !== undefined && String(v).trim() !== '';
  });

  // Ensure row has at least one valid value
  const hasValidValues = Object.values(row).some((value) =>
    value !== '' && value !== null && value !== undefined && String(value).trim() !== ''
  );

  if (!hasRequiredFields || !hasValidValues) {
    console.log(`Filtered out invalid row ${index + 1}:`, row);
    return false;
  }
  return true;
});

      if (filteredData.length === 0) {
        setError('No valid data found in CSV file. Ensure the file contains valid trade data with required fields.');
        return;
      }

      console.log('Filtered headers:', headers);
      console.log('Filtered data:', filteredData);

      const autoMappedFields = {};
      let mappingHeaders = null;

      if (detectedBroker) {
        mappingHeaders = brokerFormats[detectedBroker];
        setDetectedFormat(detectedBroker);
      } else {
        headers.forEach((header) => {
          const lowerHeader = header.toLowerCase().replace(/[\s\/]/g, '');
          if (lowerHeader.includes('entrydatetime') || lowerHeader.includes('opentime')) autoMappedFields[header] = 'entryDateTime';
          else if (lowerHeader.includes('exitdatetime') || lowerHeader.includes('closetime')) autoMappedFields[header] = 'exitDateTime';
          else if (lowerHeader === 'symbol') autoMappedFields[header] = 'symbol';
          else if (lowerHeader === 'entry' || lowerHeader.includes('openprice')) autoMappedFields[header] = 'entry';
          else if (lowerHeader === 'exit' || lowerHeader.includes('closeprice')) autoMappedFields[header] = 'exit';
          else if (lowerHeader === 'sl' || lowerHeader.includes('stoploss')) autoMappedFields[header] = 'sl';
          else if (lowerHeader === 'tp' || lowerHeader.includes('takeprofit')) autoMappedFields[header] = 'tp';
          else if (lowerHeader.includes('shortlong') || lowerHeader.includes('type') || lowerHeader.includes('side')) autoMappedFields[header] = 'shortLong';
          else if (lowerHeader.includes('lotsize') || lowerHeader.includes('volume') || lowerHeader.includes('size') || lowerHeader === 'lots') autoMappedFields[header] = 'lotSizeEntry';
          else if (lowerHeader === 'returns' || lowerHeader.includes('profit') || lowerHeader === 'pnl') autoMappedFields[header] = 'returns';
          else if (lowerHeader === 'leverage') autoMappedFields[header] = 'leverage';
          else if (lowerHeader.includes('commission')) autoMappedFields[header] = 'commissionValue';
          else if (lowerHeader === 'comments') autoMappedFields[header] = 'comments';
        });
        setDetectedFormat('Custom (fuzzy matched)');
      }

      if (mappingHeaders) {
        Object.keys(mappingHeaders).forEach((header) => {
          if (headers.includes(header)) autoMappedFields[header] = mappingHeaders[header];
        });
      }

      setFieldMapping(autoMappedFields);
      setCsvData(filteredData);
      setPreviewRows(filteredData.slice(0, 5));
      setModalOpen(true);
    },
    header: true,
    skipEmptyLines: 'greedy', // Skip empty lines and whitespace-only rows
    error: (err) => {
      console.error('CSV parsing error:', err);
      setError('Failed to parse CSV file.');
    },
  });
};

  const handleFieldMappingChange = (csvField, appField) => {
    setFieldMapping((prev) => ({ ...prev, [csvField]: appField }));
    setRefreshKey((prev) => prev + 1);
  };

  const handleSaveMappedData = async () => {
    if (!selectedAccountID) {
      setError('Please select a trade account.');
      return;
    }
  
    if (!Object.values(fieldMapping).includes('symbol') || !Object.values(fieldMapping).includes('entryDateTime')) {
      setError('Please map at least "symbol" and "entryDateTime" fields.');
      return;
    }
  
    const invalidDates = [];
  
    // Detect FTMO format to clean symbol
    const isFTMO = detectedFormat === 'ftmo';
  
    const mappedData = csvData.map((row, index) => {
      let mappedRow = {};
      Object.keys(fieldMapping).forEach((csvField) => {
        let value = row[csvField];
        if (fieldMapping[csvField] === 'shortLong') {
          value = value?.toLowerCase() === 'buy' ? 'Long' : value?.toLowerCase() === 'sell' ? 'Short' : value;
        }
        if (['entry', 'exit', 'sl', 'tp', 'leverage', 'commissionValue'].includes(fieldMapping[csvField])) {
          value = parseFloat(value) || '';
        }
        if (fieldMapping[csvField] === 'lotSizeEntry') {
          value = parseFloat(value) || 1.0;
        }
        if (fieldMapping[csvField] === 'returns') {
          const cleanedValue = value?.toString().replace(/[^0-9.-]+/g, '');
          value = !cleanedValue || isNaN(parseFloat(cleanedValue)) ? 0 : parseFloat(cleanedValue);
        }
        if (fieldMapping[csvField] === 'symbol' && isFTMO) {
          // Remove .cash suffix for FTMO symbols
          value = value ? value.replace(/\.cash$/, '') : '';
        }
        if (['entryDateTime', 'exitDateTime'].includes(fieldMapping[csvField])) {
          const dateObj = parseCustomDate(value);
          if (dateObj) {
            const date = dateObj.toISOString().split('T')[0];
            const time = dateObj.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
            mappedRow[fieldMapping[csvField]] = dateObj.toISOString();
            if (fieldMapping[csvField] === 'entryDateTime') {
              mappedRow.date = date;
              mappedRow.entryTime = time;
            } else {
              mappedRow.exitDate = date;
              mappedRow.exitTime = time;
            }
          } else {
            mappedRow[fieldMapping[csvField]] = '';
            invalidDates.push({ row: index + 1, field: csvField, value });
          }
        } else {
          mappedRow[fieldMapping[csvField]] = value || '';
        }
      });
  
      // Ensure returns is always set
      mappedRow.returns = mappedRow.returns !== undefined && !isNaN(parseFloat(mappedRow.returns)) ? parseFloat(mappedRow.returns) : 0;
  
      mappedRow.userID = userID;
      mappedRow.comments = mappedRow.comments || 'Auto-Uploaded Data';
      mappedRow.status = mappedRow.status || 'Closed';
      mappedRow.sizeType = mappedRow.sizeType || 'lot';
      mappedRow.leverage = mappedRow.leverage || 1;
      mappedRow.lotSizeEntry = mappedRow.lotSizeEntry || 1.0;
  
      return mappedRow;
    });
  
    if (invalidDates.length > 0) {
      setError(
        `Invalid dates detected in rows: ${invalidDates
          .map(d => `Row ${d.row} (${d.field}: ${d.value})`)
          .join(', ')}. Expected formats: YYYY-MM-DD HH:MM(:SS) or DD/MM/YYYY HH:MM(:SS)`
      );
      return;
    }
  
    try {
      const tradesCollection = collection(db, 'Users', userID, 'tradeAccounts', selectedAccountID, 'trades');
      const batchSize = 100;
      for (let i = 0; i < mappedData.length; i += batchSize) {
        const batch = mappedData.slice(i, i + batchSize);
        await Promise.all(batch.map((trade) => {
          console.log('Saving trade:', trade); // Log for debugging
          return addDoc(tradesCollection, trade);
        }));
      }
      setModalOpen(false);
      setCsvData([]);
      setFieldMapping({});
      setPreviewRows([]);
      setDetectedFormat(null);
      alert('Trades uploaded successfully!');
    } catch (error) {
      setError('Error saving trades: ' + error.message);
    }
  };

  const previewMappedValue = (csvField, appField, rawValue) => {
    if (!rawValue) return 'N/A';
    let value = rawValue;

    if (appField === 'shortLong') {
      value = value?.toLowerCase() === 'buy' ? 'Long' : value?.toLowerCase() === 'sell' ? 'Short' : value;
    }

    if (['entry', 'exit', 'sl', 'tp'].includes(appField)) {
      const parsedVal = parseFloat(value);
      return !isNaN(parsedVal) ? parsedVal.toFixed(5) : 'N/A';
    }

    if (appField === 'returns') {
      const parsedVal = parseFloat(value.toString().replace(/[^0-9.-]+/g, ''));
      return !isNaN(parsedVal) ? parsedVal.toFixed(2) : 'N/A';
    }

    if (['lotSizeEntry', 'leverage', 'commissionValue'].includes(appField)) {
      const parsedVal = parseFloat(value);
      return !isNaN(parsedVal) ? parsedVal.toFixed(2) : 'N/A';
    }

    if (['entryDateTime', 'exitDateTime'].includes(appField)) {
      const dateObj = parseCustomDate(value);
      return dateObj
        ? dateObj.toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })
        : 'N/A';
    }

    return value;
  };

  const defaultAccount = tradeAccounts.find((account) => account.name.toLowerCase() === 'default')?.id || tradeAccounts[0]?.id;

  React.useEffect(() => {
    if (!selectedAccountID && defaultAccount) {
      setSelectedAccountID(defaultAccount);
    }
  }, [selectedAccountID, defaultAccount, setSelectedAccountID]);

  return (
    <Container>
      <MDBox mb={2}>
        <MDTypography variant="h5" gutterBottom>Upload Trades via CSV</MDTypography>
        <MDBox display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center">
          <FormControl fullWidth sx={{ maxWidth: { sm: '200px' } }}>
            <InputLabel>Trade Account</InputLabel>
            <Select
              value={selectedAccountID || ''}
              onChange={(e) => setSelectedAccountID(e.target.value)}
              label="Trade Account"
              sx={{ height: '36px' }}
            >
              {tradeAccounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Upload a CSV file from your broker. We’ll auto-detect the format or let you map fields manually.">
            <label htmlFor="csv-upload">
              <MDButton variant="contained" color="warning" component="span" sx={{ height: '36px' }}>
                Upload CSV File
              </MDButton>
            </label>
          </Tooltip>
          <input
            accept=".csv"
            style={{ display: 'none' }}
            id="csv-upload"
            type="file"
            onChange={handleFileUpload}
          />
        </MDBox>
        <MDTypography variant="caption" mt={1}>
          Tip: Download our{' '}
          <a href="/FX_Bullsheet_Trading_Journal_CSV_Upload_Template.xlsx" download>
            Excel template
          </a>{' '}
          for faster uploads. After uploading, edit and save any trade to refresh calculations if data appears incorrect.
        </MDTypography>
      </MDBox>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {modalOpen && (
        <Paper sx={{ p: 2, backgroundColor: theme.palette.background.default, maxHeight: '80vh', overflowY: 'auto' }}>
          <MDTypography variant="h6">Map Your CSV Fields</MDTypography>
          <MDTypography variant="body2" mb={2}>
            {detectedFormat ? `Detected format: ${detectedFormat}` : 'No known format detected. Please map fields manually.'}
          </MDTypography>

          {previewRows.length > 0 && (
            <MDBox mb={2} key={refreshKey} sx={{ overflowX: 'auto' }}>
              <MDTypography variant="subtitle2">Mapped Fields Preview:</MDTypography>
              <Table size="small" sx={{ minWidth: '600px' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.background.paper }}>CSV Column</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.background.paper }}>Mapped To</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.background.paper }}>Sample Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(fieldMapping)
                    .filter(([, appField]) => appField)
                    .map(([csvField, appField]) => (
                      <TableRow key={csvField}>
                        <TableCell>
                          <MDTypography variant="body2" fontWeight="medium">
                            {csvField}
                          </MDTypography>
                        </TableCell>
                        <TableCell>
                          <MDTypography variant="body2" color="info">
                            {appField}
                          </MDTypography>
                        </TableCell>
                        <TableCell>
                          <MDTypography
                            variant="body2"
                            sx={{
                              backgroundColor: theme.palette.grey[100],
                              padding: '2px 6px',
                              borderRadius: '4px',
                              color: theme.palette.text.primary,
                            }}
                          >
                            {previewMappedValue(csvField, appField, previewRows[0][csvField])}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </MDBox>
          )}

          <Grid container spacing={2}>
            {csvData.length > 0 &&
              Object.keys(csvData[0]).map((csvField) => (
                <Grid item xs={12} sm={6} key={csvField}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>{csvField}</InputLabel>
                    <Select
                      value={fieldMapping[csvField] || ''}
                      onChange={(e) => handleFieldMappingChange(csvField, e.target.value)}
                      label={csvField}
                      sx={{ height: '36px' }}
                    >
                      <MenuItem value=""><em>Ignore</em></MenuItem>
                      {Object.keys(availableFields).map((appField) => (
                        <MenuItem key={appField} value={appField}>
                          {appField}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
          </Grid>

          <MDBox display="flex" justifyContent="space-between" mt={2}>
            <MDButton variant="contained" color="success" onClick={handleSaveMappedData} sx={{ height: '36px' }}>
              Save Trades
            </MDButton>
            <MDButton variant="outlined" color="error" onClick={() => setModalOpen(false)} sx={{ height: '36px' }}>
              Cancel
            </MDButton>
          </MDBox>
        </Paper>
      )}
    </Container>
  );
};

export default CSVUpload;